import { useEffect, useState } from 'react'
import * as pathToRegexpModule from 'path-to-regexp-6'
import { useHistory } from 'react-router'

import i18n from '@signavio/i18n'
import { IconButton, Sidebar, SidebarItem, useStyles } from '@signavio/ui'

import { prependOrg, useUserPreferences } from '@signavio/effektif-api'
import { ThemeT, utils } from '@signavio/effektif-commons'

import { DEFAULT_MIN_WIDTH_PANEL } from '../side-panel'

export function SideNavigation({
  isOpen,
  toggle,
}: {
  isOpen: boolean
  toggle: () => void
}) {
  const [activeMenuItem, setActiveMenuItem] = useState('')
  const userPreferences = useUserPreferences()
  const history = useHistory()
  const styles = useStyles(
    defaultStyles,
    {},
    {
      '&closed': !isOpen,
    }
  )

  useEffect(() => {
    if (
      [
        prependOrg('/cases/tasks/:tab'),
        prependOrg('/cases/case/:caseId/task/:taskId'),
      ].some((path) =>
        pathToRegexpModule.pathToRegexp(path).test(history.location.pathname)
      )
    ) {
      return setActiveMenuItem('tasks')
    }

    if (
      [
        prependOrg('/cases/overview/:workflowId?'),
        prependOrg('/cases/case/:caseId'),
      ].some((path) =>
        pathToRegexpModule.pathToRegexp(path).test(history.location.pathname)
      )
    ) {
      return setActiveMenuItem('cases')
    }

    if (
      [
        prependOrg('/processes/overview'),
        prependOrg('/processes/examples'),
        prependOrg('/processes/create'),
        prependOrg('/processes/process/:processId/(.*)'),
      ].some((path) =>
        pathToRegexpModule.pathToRegexp(path).test(history.location.pathname)
      )
    ) {
      return setActiveMenuItem('workflows')
    }

    if (
      [
        prependOrg('/analytics'),
        prependOrg('/analytics/report/:reportId/(.*)'),
      ].some((path) =>
        pathToRegexpModule.pathToRegexp(path).test(history.location.pathname)
      )
    ) {
      return setActiveMenuItem('analytics')
    }

    setActiveMenuItem('')
  }, [history.location.pathname])

  return (
    <div {...styles} data-testid="side-navigation">
      {
        <Sidebar wideningFactor={0} style={styles('menuItems')}>
          <SidebarItem
            active={activeMenuItem === 'tasks'}
            icon="task-complex"
            onClick={() =>
              history.push(
                prependOrg(
                  `/cases/tasks/${userPreferences?.tasksTab || 'inbox'}`
                )
              )
            }
            style={styles('menuItem')}
          >
            {isOpen ? i18n('Tasks') : ''}
          </SidebarItem>
          <SidebarItem
            active={activeMenuItem === 'cases'}
            icon="case"
            onClick={() => {
              return history.push(
                prependOrg(
                  `/cases/overview${
                    userPreferences?.casesProcessId
                      ? `/${userPreferences.casesProcessId}`
                      : ''
                  }`
                )
              )
            }}
            style={styles('menuItem')}
          >
            {isOpen ? i18n('Cases') : ''}
          </SidebarItem>
          <SidebarItem
            active={activeMenuItem === 'workflows'}
            icon="workflow"
            onClick={() => history.push(prependOrg('/processes'))}
            style={styles('menuItem')}
          >
            {isOpen ? i18n('Processes') : ''}
          </SidebarItem>
          <SidebarItem
            active={activeMenuItem === 'analytics'}
            icon="report"
            onClick={() => history.push(prependOrg('/analytics'))}
            style={styles('menuItem')}
          >
            {isOpen ? i18n('Analytics') : ''}
          </SidebarItem>
        </Sidebar>
      }
      <div {...styles('toggle')}>
        <IconButton
          aria-label={isOpen ? 'close-navigation' : 'open-navigation'}
          icon={isOpen ? 'panel-left-collapse' : 'panel-right-collapse'}
          onClick={toggle}
        />
      </div>
    </div>
  )
}

const defaultStyles = ({ font, lineHeight, spacing }: ThemeT) => {
  const headerHeight = utils.calculateHeight(
    font.size.normal,
    lineHeight,
    spacing.small
  )

  return {
    backgroundColor: 'white',
    height: `calc(100vh - ${headerHeight}px)`,
    position: 'fixed',
    zIndex: 999,
    menuItems: {
      width: '100%',
      marginRight: 0,
      paddingRight: 0,
    },

    menuItem: {
      borderRadius: 0,
      width: DEFAULT_MIN_WIDTH_PANEL,
    },

    toggle: {
      bottom: spacing.xxsmall,
      position: 'absolute',
      right: spacing.xxsmall,
    },

    '&closed': {
      width: headerHeight,

      menuItems: {
        width: `${headerHeight}px !important`,
      },

      menuItem: {
        width: 'calc(100% + 1px)',
        paddingLeft: spacing.xxsmall,
        paddingRight: 0,
        justifyContent: 'center',
        borderRadius: 0,
      },

      toggle: {
        left: '50%',
        right: 'auto',
        transform: 'translateX(-50%)',
      },
    },
  }
}
